import React from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {{
 *   errorHandler: () => React.ReactNode;
 *   errorLogger: (error: Error, info: React.ErrorInfo) => void;
 *   children: (React.ReactElement & { type?: { displayName: string; }}) | null;
 * }} ErrorBoundaryProps
 */

/**
 * @augments {React.Component<ErrorBoundaryProps, { hasError: boolean }>}
 */
export class ErrorBoundary extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    errorHandler: PropTypes.func,
    errorLogger: PropTypes.func,
  };

  static defaultProps = {
    errorHandler: null,
    errorLogger: null,
  };

  /**
   * @param {ErrorBoundaryProps} props
   */
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  /**
   * @override
   * @param {Error} error
   * @param {React.ErrorInfo} info
   */
  componentDidCatch(error, info) {
    const { children, errorLogger } = this.props;
    const childCompName = children?.type?.displayName;

    // doing this the quick & dirty way because we shouldn't be logging very much
    // throughout our app, and using other methods loses context of line numbers
    // eslint-disable-next-line no-console
    if (typeof console !== 'undefined' && typeof console.error === 'function') {
      // eslint-disable-next-line no-console
      console.error(`error in component: ${childCompName}`, error, info);
    }

    if (errorLogger) errorLogger(error, info);

    this.setState({ hasError: true });
  }

  /**
   * @override
   */
  render() {
    const { hasError } = this.state;
    const { children, errorHandler } = this.props;

    if (hasError) {
      if (errorHandler) {
        return errorHandler() || null;
      }
      return null;
    }

    return children;
  }
}
